/**
 * Angaben des Prüfbüros.
 *
 * Zwei Zwecke in einem Bildschirm, und das ist Absicht: der Briefkopf steht
 * auf jedem Prüfbericht, die kaufmännischen Angaben auf jeder Rechnung. Beide
 * beschreiben dieselbe Firma, und wer umzieht, will nicht an zwei Stellen
 * dasselbe eintragen.
 *
 * Ganz oben steht, ob die Angaben für eine Rechnung reichen. Nach § 14 UStG
 * muss eine Rechnung Name, vollständige Anschrift und Steuernummer oder
 * USt-IdNr tragen. Fehlt eines davon, kann der Empfänger die Vorsteuer nicht
 * ziehen – bei einer Gemeinde kommt die Rechnung dann zurück. Das soll man
 * hier sehen und nicht erst, wenn die Rechnung geschrieben ist.
 */

import { useEffect, useState } from 'react'
import { useMandant, useMandantAendern } from '../api/verwaltung'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'

type Werte = Record<string, string | boolean>

export function Unternehmen() {
  const mandant = useMandant()
  const speichern = useMandantAendern()
  const [stand, setzeStand] = useState<Werte>({})
  const [anfang, setzeAnfang] = useState<Werte>({})
  const [gespeichert, setzeGespeichert] = useState(false)

  useEffect(() => {
    if (!mandant.data) return
    const d = mandant.data as unknown as Record<string, unknown>
    const werte: Werte = {}
    for (const feld of FELDER) {
      const wert = d[feld.name]
      werte[feld.name] = feld.art === 'ja-nein' ? Boolean(wert) : wert == null ? '' : String(wert)
    }
    setzeStand(werte)
    setzeAnfang(werte)
  }, [mandant.data])

  if (mandant.isLoading) return <Ladeanzeige />
  if (mandant.error) return <Fehlerhinweis fehler={mandant.error} />

  const daten = mandant.data!
  const geaendert = FELDER.filter((f) => stand[f.name] !== anfang[f.name])

  function absenden() {
    const nutzlast: Record<string, unknown> = {}
    for (const feld of geaendert) {
      const wert = stand[feld.name]
      if (typeof wert === 'boolean') nutzlast[feld.name] = wert
      else if (wert.trim() === '') nutzlast[feld.name] = feld.pflicht ? wert : null
      else nutzlast[feld.name] = feld.art === 'zahl' ? Number(wert) : wert.trim()
    }
    setzeGespeichert(false)
    speichern.mutate(nutzlast, { onSuccess: () => setzeGespeichert(true) })
  }

  return (
    <div className="space-y-4">
      {daten.rechnungsfaehig ? (
        <p
          role="status"
          className="rounded-feld border-2 border-stufe-ob bg-stufe-ob-grund p-3 text-stufe-ob"
        >
          <span className="font-semibold">Die Angaben reichen für eine Rechnung.</span>{' '}
          Ob sie stimmen, kann nur das Prüfbüro selbst beurteilen.
        </p>
      ) : (
        <div
          role="status"
          className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3 text-stufe-2"
        >
          <p className="font-semibold">
            Für eine Rechnung fehlen noch Pflichtangaben nach § 14 UStG.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {daten.fehlende_pflichtangaben.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
          <p className="mt-1 text-slate-700">
            Ohne sie kann der Empfänger die Vorsteuer nicht ziehen – Gemeinden schicken
            solche Rechnungen zurück.
          </p>
        </div>
      )}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          absenden()
        }}
      >
        {GRUPPEN.map((gruppe) => (
          <fieldset key={gruppe.titel} className="karte">
            <legend className="px-1 text-lg font-bold">{gruppe.titel}</legend>
            {gruppe.hinweis && (
              <p className="mb-3 text-base text-slate-600">{gruppe.hinweis}</p>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              {FELDER.filter((f) => f.gruppe === gruppe.titel).map((feld) => {
                const kennung = `u-${feld.name}`
                const wert = stand[feld.name]
                const wurdeGeaendert = stand[feld.name] !== anfang[feld.name]

                if (feld.art === 'ja-nein') {
                  return (
                    <label
                      key={feld.name}
                      className="flex items-start gap-3 sm:col-span-2"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(wert)}
                        onChange={(e) =>
                          setzeStand((v) => ({ ...v, [feld.name]: e.target.checked }))
                        }
                        className="mt-1 h-6 w-6"
                      />
                      <span className="font-normal">
                        <span className="font-semibold">{feld.beschriftung}</span>
                        {feld.hinweis && (
                          <span className="block text-base text-slate-600">
                            {feld.hinweis}
                          </span>
                        )}
                      </span>
                    </label>
                  )
                }

                return (
                  <div key={feld.name} className={feld.breit ? 'sm:col-span-2' : undefined}>
                    <label htmlFor={kennung}>
                      {feld.beschriftung}
                      {wurdeGeaendert && (
                        <span className="ml-2 font-normal text-akzent-600">geändert</span>
                      )}
                    </label>
                    {feld.art === 'mehrzeilig' ? (
                      <textarea
                        id={kennung}
                        value={String(wert ?? '')}
                        onChange={(e) =>
                          setzeStand((v) => ({ ...v, [feld.name]: e.target.value }))
                        }
                        rows={3}
                        maxLength={feld.laenge}
                      />
                    ) : (
                      <input
                        id={kennung}
                        type={feld.art === 'zahl' ? 'number' : 'text'}
                        inputMode={feld.art === 'zahl' ? 'numeric' : undefined}
                        step={feld.name === 'steuersatz' ? '0.01' : undefined}
                        value={String(wert ?? '')}
                        onChange={(e) =>
                          setzeStand((v) => ({ ...v, [feld.name]: e.target.value }))
                        }
                        maxLength={feld.art === 'zahl' ? undefined : feld.laenge}
                        autoCapitalize={feld.name === 'email' ? 'none' : undefined}
                        placeholder={feld.beispiel}
                      />
                    )}
                    {feld.hinweis && (
                      <p className="mt-1 text-base text-slate-600">{feld.hinweis}</p>
                    )}
                  </div>
                )
              })}
            </div>
          </fieldset>
        ))}

        <Fehlerhinweis fehler={speichern.error} />

        <div className="sticky bottom-20 flex items-center gap-3 rounded-feld border-2 border-slate-200 bg-white p-3 sm:bottom-4">
          <button
            type="submit"
            className="knopf-haupt"
            disabled={speichern.isPending || geaendert.length === 0}
          >
            {speichern.isPending
              ? 'Speichert …'
              : geaendert.length === 0
                ? 'Nichts geändert'
                : `Speichern (${geaendert.length})`}
          </button>
          {gespeichert && geaendert.length === 0 && (
            <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
              Gespeichert
            </span>
          )}
        </div>
      </form>
    </div>
  )
}

type Feldbeschreibung = {
  name: string
  beschriftung: string
  gruppe: string
  art?: 'text' | 'zahl' | 'mehrzeilig' | 'ja-nein'
  hinweis?: string
  beispiel?: string
  breit?: boolean
  laenge?: number
  pflicht?: boolean
}

const GRUPPEN = [
  { titel: 'Firma', hinweis: 'Steht im Briefkopf des Berichts und auf jeder Rechnung.' },
  {
    titel: 'Steuer',
    hinweis:
      '§ 14 Abs. 4 Nr. 2 UStG verlangt eines von beiden – Steuernummer oder USt-IdNr.',
  },
  { titel: 'Bank' },
  { titel: 'Dokumente' },
]

const FELDER: Feldbeschreibung[] = [
  { name: 'name', beschriftung: 'Firmenname', gruppe: 'Firma', breit: true, pflicht: true, laenge: 200 },
  { name: 'inhaber', beschriftung: 'Inhaber', gruppe: 'Firma', laenge: 120 },
  { name: 'zertifikat', beschriftung: 'Zertifikat des Büros', gruppe: 'Firma', laenge: 60, beispiel: 'QSP-AGIS-00124' },
  { name: 'strasse', beschriftung: 'Straße und Hausnummer', gruppe: 'Firma', breit: true, laenge: 150 },
  { name: 'plz', beschriftung: 'PLZ', gruppe: 'Firma', laenge: 10 },
  { name: 'ort', beschriftung: 'Ort', gruppe: 'Firma', laenge: 100 },
  { name: 'land', beschriftung: 'Land', gruppe: 'Firma', laenge: 2, hinweis: 'Zwei Buchstaben, etwa DE.' },
  { name: 'telefon', beschriftung: 'Telefon', gruppe: 'Firma', laenge: 50 },
  { name: 'email', beschriftung: 'E-Mail', gruppe: 'Firma', laenge: 254 },
  { name: 'webseite', beschriftung: 'Webseite', gruppe: 'Firma', laenge: 200, beispiel: 'https://spuer.net' },
  {
    name: 'briefkopf',
    beschriftung: 'Briefkopfzeile',
    gruppe: 'Firma',
    art: 'mehrzeilig',
    breit: true,
    laenge: 300,
    hinweis: 'Eine Zeile über dem Prüfbericht, Teile mit „|" getrennt.',
  },

  { name: 'steuernummer', beschriftung: 'Steuernummer', gruppe: 'Steuer', laenge: 40, beispiel: '58/137/05429' },
  { name: 'ust_id', beschriftung: 'USt-IdNr', gruppe: 'Steuer', laenge: 20, beispiel: 'DE123456789' },
  {
    name: 'kleinunternehmer',
    beschriftung: 'Kleinunternehmer nach § 19 UStG',
    gruppe: 'Steuer',
    art: 'ja-nein',
    hinweis:
      'Dann weist die Rechnung keine Umsatzsteuer aus, sondern trägt den vorgeschriebenen Hinweis. Das ist etwas anderes als 0 % – das wäre eine Steuerbefreiung.',
  },
  {
    name: 'steuersatz',
    beschriftung: 'Regelsteuersatz in Prozent',
    gruppe: 'Steuer',
    hinweis: 'Wird bei Kleinunternehmern nicht verwendet.',
    beispiel: '19.00',
  },

  { name: 'kontoinhaber', beschriftung: 'Kontoinhaber', gruppe: 'Bank', laenge: 120 },
  { name: 'bank', beschriftung: 'Bank', gruppe: 'Bank', laenge: 120 },
  { name: 'iban', beschriftung: 'IBAN', gruppe: 'Bank', laenge: 34 },
  { name: 'bic', beschriftung: 'BIC', gruppe: 'Bank', laenge: 11 },

  {
    name: 'zahlungsziel_tage',
    beschriftung: 'Zahlungsziel in Tagen',
    gruppe: 'Dokumente',
    art: 'zahl',
  },
  {
    name: 'glaeubiger_id',
    beschriftung: 'Gläubiger-Identifikation',
    gruppe: 'Dokumente',
    laenge: 35,
    hinweis: 'Nur nötig, wenn per Lastschrift eingezogen wird.',
  },
  {
    name: 'fusstext',
    beschriftung: 'Fußtext auf Dokumenten',
    gruppe: 'Dokumente',
    art: 'mehrzeilig',
    breit: true,
    hinweis: 'Erscheint unter jedem Angebot, jeder Auftragsbestätigung und Rechnung.',
  },
]
